import { watch } from 'vue'

import { useMainStore } from '@/stores/main'
import { defineStore } from 'pinia'

import { orderBy } from 'lodash-es'

import {
  CartWriteUnconfirmedError,
  isCartBundleNotFoundError,
  isCartDependencyError,
  isCartGoneError,
  isCartMutationInProgressError,
  isCartUpsertConcurrencyError,
  isRequestTimeoutError
} from '@/lib/cartErrors'
import { downloadAllBundleConstants } from '@/lib/constants'
import { retouchingBundleConstants } from '@/lib/constants'
import { tabHoldsPayment } from '@/lib/paymentHoldMarker'
import { findUnderclassBackgroundImageUrl } from '@/lib/utils'
import { appErrorService } from '@/services/AppErrorService'
import Sunny16CommerceCartService from '@/services/Sunny16CommerceCartService'

import { CadyMarket } from '@/types/CadyMarket'
import { CartBundleConfigurator } from '@/types/CartBundleConfigurator'
import { GoogleAnalyticsCartItem } from '@/types/GoogleAnalyticsCartItem'
import type { UCBackgroundResult } from '@/types/UCBackgroundResult'

import { CartLockReason, CartSource } from '@VelocitySquared/sunny16-commerce-dataobjects/enums'
import type {
  CartBundleStorefrontDto,
  CartItemCustomizationDto,
  CartItemImageCustomizationDto,
  CartStorefrontDto,
  CartStorefrontValidationResultDto,
  ImagePromptResponseDto,
  MediaReferenceDto,
  PromoCodeResultStorefrontDto,
  PromptResponseDto,
  QualifiedConditionalOfferStorefrontDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'
import {
  ApplyPromoCodeStorefrontDto,
  BundleStorefrontDto,
  CartBundleUpdateStorefrontDto,
  CartBundleUpsertStorefrontDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

const sunny16CommerceCartService = new Sunny16CommerceCartService()

// `recheckPaymentHold`'s refetch while one is in flight: a tab coming back into view fires both
// `visibilitychange` and `focus`, and the second call joins the first rather than fetching again.
let paymentHoldRecheck: Promise<void> | null = null

// Wraps a cart-mutating action so `inFlight` reflects the busy window across
// the entire awaited body, including composite sequences. UI controls bind
// to the derived `isBusy` getter to gate user taps. See decision
// `docs/decisions/2026.05.12-001-cart-store-is-busy-serialization.md`.
//
// A call that outlives `resetStore()` leaves the counter alone: the reset already zeroed it, and
// decrementing again would drive it negative and read as idle during the next session's writes.
async function runWithBusy<T>(
  store: { inFlight: number; generation: number },
  fn: () => Promise<T>
): Promise<T> {
  const generation = store.generation
  store.inFlight++
  try {
    return await fn()
  } finally {
    if (store.generation === generation) store.inFlight--
  }
}

// Entries written before the Portfolio auto-add latch moved into this store hold it in the `main`
// entry. Read it only while the `cart` entry predates the move: once this store has saved the latch,
// including `resetStore()` clearing it for a new session, that value wins. Removable once released
// entries have rolled over.
function readLegacyPortfolioLatch(): boolean {
  try {
    const cart = JSON.parse(localStorage.getItem('cart') ?? 'null')
    if (cart && 'portfolioAutoAddAttemptedThisSession' in cart) return false

    return (
      JSON.parse(localStorage.getItem('main') ?? 'null')?.portfolioAutoAddAttemptedThisSession ===
      true
    )
  } catch {
    return false
  }
}

/** What {@link runCartWrite} needs from the store beyond its state. */
interface CartWriteRecovery {
  refreshCart: () => Promise<unknown>
}

/**
 * Errors that are evidence the server did **not** change the cart, so the local copy is still
 * accurate and a refetch would be a wasted call. Everything else is treated as "the cart may have
 * moved": Sunny16 commits a config save's selections before its customizations run, so even a 400
 * can leave the cart different from what the storefront last saw.
 *
 * A busy-cart 409 refused the write, but only a `Mutation` one skips the refetch. After a `Payment`
 * one, the refetched cart carries the hold.
 */
function leavesCartUnchanged(error: unknown): boolean {
  return (
    isCartDependencyError(error) ||
    isCartUpsertConcurrencyError(error) ||
    isCartMutationInProgressError(error)
  )
}

/**
 * Wraps a cart write so a failure refetches the cart before the customer is told anything.
 *
 * The client cannot tell whether a failed write changed the cart, so it asks. A timed-out write is
 * the sharpest case — **unknown**, not failed, because the server may have committed it — and
 * reporting it as a failure is exactly how the storefront's persisted cart ends up describing a
 * cart the server no longer has, with nothing to ever revalidate it (2026-09-16). Whatever the
 * customer is shown afterwards is then the server's answer rather than a stale local guess.
 *
 * A failed refetch is swallowed: the caller still needs the original error, and the refresh failing
 * tells them nothing more useful than that error already did.
 */
async function runCartWrite<T>(store: CartWriteRecovery, fn: () => Promise<T>): Promise<T> {
  try {
    return await fn()
  } catch (error) {
    if (leavesCartUnchanged(error)) throw error

    try {
      await store.refreshCart()
    } catch {
      // Deliberately swallowed; the error below is the useful one.
    }

    throw isRequestTimeoutError(error) ? new CartWriteUnconfirmedError({ cause: error }) : error
  }
}

/**
 * Wraps a cart-mutating action so it also **announces itself** — see {@link CartMutationRecord}.
 *
 * On top of `runWithBusy`, it captures the subtotal the customer acted against and stamps a new record
 * once the action has actually changed the cart. "Actually changed" is a `cartData` reference
 * comparison, plus `fn` calling `unchanged()` on the silent-success paths: the upsert concurrency
 * no-op and the swallowed duplicate-DELETE. The comparison alone is not enough there, because those
 * paths are races — the winning call has usually reassigned `cartData` by the time the loser settles,
 * and one customer tap would otherwise announce twice.
 *
 * Only actions a *customer* takes on their cart contents use this. Cart loads, `prepareCart` and
 * `validateCart` reassign `cartData` as well, and a consumer that treated those as mutations would open
 * and animate the flyout on page load.
 */
async function runCartMutation<T>(
  store: CartStore & CartWriteRecovery,
  details: { kind: CartMutationKind; suppressCartFlyout?: boolean | (() => boolean) },
  fn: (unchanged: () => void) => Promise<T>
): Promise<T> {
  const previousSubtotal = store.cartData?.subtotalPostDiscounts ?? 0
  const cartBefore = store.cartData
  let changedNothing = false

  return runWithBusy(store, async () => {
    const result = await runCartWrite(store, () =>
      fn(() => {
        changedNothing = true
      })
    )

    if (!changedNothing && store.cartData !== cartBefore) {
      store.lastCartMutation = {
        id: (store.lastCartMutation?.id ?? 0) + 1,
        previousSubtotal,
        kind: details.kind,
        // Resolved here rather than at the call, so a caller whose answer depends on what the
        // response granted can supply a thunk. Evaluated against the *new* `cartData` — this runs
        // after `fn()`, and only when the cart actually changed.
        suppressCartFlyout:
          (typeof details.suppressCartFlyout === 'function'
            ? details.suppressCartFlyout()
            : details.suppressCartFlyout) ?? false
      }
    }

    return result
  })
}

// Builds the CartAttributes dictionary stamped onto every cart bundle upsert. Currently carries the cadySessionToken
// the user entered the site with, keyed identically to the order metadata field (`order.metadata.cadySessionToken`).
function buildCartUpsertAttributes(cadySessionToken: string | null): { [key: string]: string } {
  return { cadySessionToken: cadySessionToken ?? '' }
}

/**
 * The last completed customer cart mutation — the "a run should start" signal the reward-unlock
 * sequence hangs off (SUN-292 A2b), and the only place the pre-mutation subtotal survives.
 *
 * **Why the store publishes this rather than the flyout watching the subtotal.** A subtotal watcher
 * cannot tell a customer's add from the cart arriving: a persisted cart the server has re-priced
 * changes the subtotal on load, which would look exactly like an add and — once auto-open lands —
 * would fly the flyout open at page load. Only the mutating actions know a customer did something, so
 * they are what says so.
 */
export interface CartMutationRecord {
  /** Monotonic, so two mutations leaving the cart identical are still two runs rather than one. */
  id: number
  /** Effective subtotal immediately before the mutation — the sequence's `fromPct` is a function of it. */
  previousSubtotal: number
  /** What the customer did. Only an {@link CartMutationKind.Add} opens the flyout by itself. */
  kind: CartMutationKind
  /**
   * This particular mutation must not open the flyout, whatever the route allows (SUN-292 Gap 20).
   *
   * The enhancement gate is *positional* — it answers where the customer is. This is the causal axis
   * the gate deliberately refuses to carry: an add taken inside the upsell pop-up happens on the very
   * routes that otherwise auto-open, and must not, because the customer is being moved on to the next
   * page and a drawer would open over a page already navigating away.
   *
   * Set from the `upsellId` the caller already passes, which *is* the per-mutation signal the gate ADR
   * asks for — "this add came from an upsell offer" is a property of the mutation, not of the page. No
   * second flag to keep in agreement with it.
   *
   * The retouching step's reward claim is the second cause routed through here, and the one that made
   * the suppression late-evaluated: an add that grants a reward *requiring configuration* opens the
   * claim modal, and the flyout must not celebrate behind it. Whether it did is knowable only from the
   * response, so that caller passes a thunk rather than a boolean.
   */
  suppressCartFlyout: boolean
}

/**
 * What a {@link CartMutationRecord} describes. The sequence runs on any of them — a remove pulls the
 * rail backwards — but only `Add` opens a closed flyout: the business asked for the cart to open "on
 * every add", and a cart that flew open because the customer *deselected* a package would be answering
 * a question nobody asked.
 */
export enum CartMutationKind {
  Add = 'add',
  /** A quantity change. Reached by the decrement path as well, so it is not an add in disguise. */
  Update = 'update',
  Remove = 'remove',
  Empty = 'empty'
}

interface CartStore {
  cartData: CartStorefrontDto | null
  inFlight: number
  // Bumped by `resetStore()` so a call started before the reset can tell its result is stale.
  generation: number
  lastCartMutation: CartMutationRecord | null
  // Latches once the data loader's `reconcilePortfolioPresence()` has run for the current
  // subject. The reconcile is a one-shot attempt at subject-load time; any later
  // `dataLoader.execute()` in the same session finds the flag set and bails before doing any
  // cart work. Persisted so a refresh does not re-attempt; cleared by `resetStore()` on a new
  // session token.
  portfolioAutoAddAttemptedThisSession: boolean
  // The `cartId` whose first `/prepare` has already run with in-cart upsells
  // enabled, or null when no cart has been prepared yet. Keyed on the cart
  // rather than the session so a new cart created after an order in the same
  // session gets its own first prepare. Persisted, so a refresh or a return
  // to checkout does not count as a first landing. See `prepareCart()`.
  inCartUpsellsAppliedForCartId: string | null
}

export const useCartStore = defineStore('cart', {
  state: (): CartStore => ({
    // All state properties should be added to the manual resetStore action method below
    cartData: null,
    inFlight: 0,
    generation: 0,
    lastCartMutation: null,
    portfolioAutoAddAttemptedThisSession: false,
    inCartUpsellsAppliedForCartId: null
  }),
  getters: {
    /**
     * True whenever at least one cart-mutating action is in flight. UI cart
     * controls should bind their `disabled` state to this getter so rapid
     * taps and racing handlers can't fire duplicate cart calls.
     */
    isBusy(state: CartStore): boolean {
      return state.inFlight > 0
    },
    /**
     * True while Sunny16 holds the cart for a payment attempt — from `begin-payment` until the
     * payment succeeds or the hold is released. The server refuses every cart mutation meanwhile.
     * Read off the last cart fetched, so another tab's hold shows only once this tab refetches.
     */
    isHeldForPayment(state: CartStore): boolean {
      return (
        !!state.cartData?.isOperationallyLocked &&
        state.cartData.operationalLockReason === CartLockReason.Payment
      )
    },
    /**
     * True while a cart change would race or be refused: one is in flight, or the cart is held for a
     * payment. Cart-mutation controls bind `disabled` to this; `isBusy` stays the in-flight signal
     * for spinners and for the Pay button, which takes the hold itself.
     */
    mutationsBlocked(): boolean {
      return this.isBusy || this.isHeldForPayment
    },
    cartId(state: CartStore): string | undefined {
      return state.cartData?.cartId
    },
    cartSecurityKey(state: CartStore): string | undefined {
      return state.cartData?.securityKey
    },
    cartSubtotal(state: CartStore): string | null {
      if (!state.cartData) return null

      return state.cartData.subtotalPostDiscounts.toFixed(2)
    },
    /**
     * Post-discount cart subtotal as a number, `0` when the cart is empty/unloaded. This is the value
     * the reward ladder, free-gift, and quick-add thresholds are measured against — the single source
     * of truth consumed by `useRewards`, `useFreeGift`, `useQuickAdds`, and the crossing detectors.
     */
    effectiveSubtotal(state: CartStore): number {
      return state.cartData?.subtotalPostDiscounts ?? 0
    },
    catalogBundleIsInCart(state: CartStore) {
      return (catalogBundleId: string) =>
        state.cartData?.cartBundles
          .map((cartBundle: CartBundleStorefrontDto) => cartBundle.catalogBundleId)
          .includes(catalogBundleId)
    },
    cartBundleIdIsInCart(state: CartStore) {
      return (cartBundleId: string) =>
        state.cartData?.cartBundles
          .map((cartBundle: CartBundleStorefrontDto) => cartBundle.cartBundleId)
          .includes(cartBundleId)
    },
    cartHasBundles(state: CartStore) {
      if (state.cartData?.cartBundles) {
        return state.cartData.cartBundles.length > 0
      }
      return false
    },
    cartBundles(state: CartStore): CartBundleStorefrontDto[] {
      return state.cartData?.cartBundles || []
    },
    /** Total item quantity across all cart bundles — the value shown in the app-header cart badge. */
    totalCartQuantity(state: CartStore): number {
      return (
        state.cartData?.cartBundles?.reduce(
          (total: number, bundle: CartBundleStorefrontDto) => total + bundle.quantity,
          0
        ) ?? 0
      )
    },
    cartBundlesAsGoogleAnalyticsCartItems(): GoogleAnalyticsCartItem[] {
      return this.cartBundles.map((cb) => {
        return {
          id: cb.bundleIdentifier,
          name: cb.bundleNameInternal,
          price: cb.bundleTotalPostDiscounts,
          quantity: cb.quantity
        } as GoogleAnalyticsCartItem
      })
    },
    getCartBundleByCatalogBundleId(state: CartStore) {
      return (catalogBundleId: string) => {
        return state.cartData?.cartBundles.find(
          (cartBundle) => cartBundle.catalogBundleId === catalogBundleId
        )
      }
    },
    /**
     * Cart-conditional auto-discount lookup for the anticipatory tile price. Returns the qualified conditional
     * offer for a given catalog bundle's `bundleId`, or `null` when the cart advertises none. The backend
     * recomputes `qualifiedConditionalOffers` on every cart mutation and an entry exists only while the cart
     * qualifies AND the target bundle is not yet in the cart, so this is derived fresh from `cartData` (never
     * cached). When a bundle matches more than one offer we surface the deepest discount (lowest `offerPrice`).
     * See `docs/features/2026.07.06-senior-market/implementation-plans/2026.06.17-001-cart-conditional-auto-discounts.md`.
     *
     * Matching key: an offer's `targetBundleId` is the bundle's `bundleId` (the catalog/product identity), NOT the
     * per-cart `catalogBundleId` line key used elsewhere in this store (e.g. `catalogBundleIsInCart`). Callers must
     * therefore pass `catalogBundle.bundleId`. The two ids look interchangeable but are not — passing the wrong one
     * makes every lookup miss silently (no error, no type failure), so keep this aligned with the backend contract.
     */
    getConditionalOfferByBundleId(state: CartStore) {
      return (bundleId: string): QualifiedConditionalOfferStorefrontDto | null => {
        const offers = state.cartData?.qualifiedConditionalOffers
        if (!offers || offers.length === 0) {
          return null
        }
        return offers
          .filter((offer) => offer.targetBundleId === bundleId)
          .reduce<QualifiedConditionalOfferStorefrontDto | null>((lowest, offer) => {
            return lowest === null || offer.offerPrice < lowest.offerPrice ? offer : lowest
          }, null)
      }
    },
    isCustomerServiceCart(): boolean {
      if (!this.cartData) {
        return false
      }
      return this.cartData?.source === CartSource.CustomerService
    },
    paymentOnOrderIsRequired(): boolean {
      if (!this.cartData) {
        return true
      }

      return this.cartData?.source !== CartSource.CustomerService
    },
    lastAddedCartBundles(state: CartStore): CartBundleStorefrontDto[] {
      return (
        state.cartData?.cartBundles.filter((cartBundle) => {
          return state.cartData?.newCartBundleIds.includes(cartBundle.cartBundleId)
        }) || []
      )
    },
    lastAddedCartBundlesAsGoogleAnalyticsCartItems(): GoogleAnalyticsCartItem[] {
      return this.lastAddedCartBundles.map((cb) => {
        return {
          id: cb.bundleIdentifier,
          name: cb.bundleNameInternal,
          price: cb.bundleTotalPostDiscounts,
          quantity: cb.quantity
        } as GoogleAnalyticsCartItem
      })
    },
    cartContainsWallets(state: CartStore): boolean {
      return state.cartData?.containsWallets ?? false
    },
    underclassBackgroundImageValue(state: CartStore): string | null {
      if (!this.cartHasBundles) {
        return null // if there are no cart bundles there's definitely no background image value
      }

      // Get the cart bundle with the UC_Background identifier
      const cartBundle = state.cartData?.cartBundles.find(
        (cb) => cb.bundleIdentifier === 'UC_Background'
      )
      if (!cartBundle) {
        return null // if there's no underclass background bundle in the cart, return null
      }

      // Get the selected background color value from the cart bundle
      const backgroundItem = getUCBackgroundItem(cartBundle)
      return backgroundItem?.value ?? null
    },
    adHocCartBundlePreviewImageUrl(state: CartStore): (cartBundleId: string) => string | null {
      return (cartBundleId: string) => {
        const cartBundle = state.cartData?.cartBundles.find(
          (cartBundle) => cartBundle.cartBundleId === cartBundleId
        )

        if (!cartBundle) {
          return null
        }

        if (cartBundle.bundleIdentifier === 'UC_Background') {
          // Reads through `getUCBackgroundItem` rather than repeating its three-level dereference:
          // this getter used to walk `cartItems[0].cartItemCustomizationDetails.promptResponses[0]`
          // itself, unguarded, and threw on an incomplete bundle. It runs on the cart row render
          // path (`CartBundleItem`, `AppCartSidebarBundleItem`), so the throw took the row's whole
          // render down rather than just dropping a thumbnail.
          const backgroundItem = getUCBackgroundItem(cartBundle)

          if (backgroundItem) {
            return findUnderclassBackgroundImageUrl(backgroundItem.value)
          }
        }

        return null
      }
    },
    selectedMediaItemsInCart(state: CartStore): string[] {
      if (!state.cartData?.cartBundles) {
        return []
      }

      const canonicalUrls: string[] = []

      if (this.cartContainsDigitalDownloadAll) {
        // If the cart contains the DIGITAL_ALL item, we need to include all subject media CanonicalUrls from main store
        const mainStore = useMainStore()

        if (mainStore.galleryImages) {
          mainStore.galleryImages.forEach((mediaItem) => {
            if (mediaItem.CanonicalUrl) {
              canonicalUrls.push(mediaItem.CanonicalUrl)
            }
          })
        }
      } else {
        state.cartData.cartBundles.forEach((cartBundle) => {
          cartBundle.cartItems.forEach((cartItem) => {
            cartItem.cartItemCustomizationDetails.promptResponses.forEach((promptResponse) => {
              // Check if this is an image prompt response by looking for MediaReferenceDto properties
              const imagePromptResponse = promptResponse as ImagePromptResponseDto
              if (
                imagePromptResponse.value &&
                typeof imagePromptResponse.value === 'object' &&
                'canonicalUrl' in imagePromptResponse.value
              ) {
                canonicalUrls.push((imagePromptResponse.value as MediaReferenceDto).canonicalUrl)
              }
            })
          })
        })
      }

      // Return unique canonical URLs
      return [...new Set(canonicalUrls)]
    },
    cartContainsDigitalDownloadAll(state: CartStore): boolean {
      return (
        state.cartData?.cartBundles?.some((cb) => {
          return cb.cartItems.some(
            (ci) => ci.item?.identifier === downloadAllBundleConstants.DOWNLOAD_ALL_ITEM_IDENTIFIER
          )
        }) || false
      )
    },
    /**
     * Whether the cart contains media items eligible for retouching.
     * A media item is retouchable if it is tagged with 'retouching-include'.
     */
    hasRetouchableMediaItemsInCart(): boolean {
      const mainStore = useMainStore()
      return mainStore
        .subjectMediaItemsCanonicalByCanonicalUrl(this.selectedMediaItemsInCart)
        .some((mediaItem) =>
          mediaItem.MediaTags?.includes(retouchingBundleConstants.RETOUCHING_INCLUDE_MEDIA_TAG)
        )
    },
    newBundlesNeedingConfiguration(state: CartStore): CartBundleStorefrontDto[] {
      if (
        state.cartData?.cartBundles.length === 0 ||
        state.cartData?.newCartBundleIds.length === 0 ||
        state.cartData?.configurationRequiredCartBundleIds.length === 0
      ) {
        return []
      }

      return (
        state.cartData?.cartBundles.filter(
          (cartBundle) =>
            state.cartData?.newCartBundleIds.includes(cartBundle.cartBundleId) &&
            state.cartData?.configurationRequiredCartBundleIds.includes(cartBundle.cartBundleId)
        ) || []
      )
    }
  },
  actions: {
    async addCartBundle(
      catalogBundleId: string,
      bundle: BundleStorefrontDto,
      quantity: number,
      upsellId?: string
    ) {
      // An `upsellId` means the add was taken inside the upsell pop-up, which must not open the
      // flyout — the customer is being advanced to the next page instead (Gap 20).
      return runCartMutation(
        this,
        { kind: CartMutationKind.Add, suppressCartFlyout: !!upsellId },
        async (unchanged) => {
          const mainStore = useMainStore()
          const upsertData = new CartBundleUpsertStorefrontDto()

          upsertData.cartBundleId = ''
          upsertData.catalogBundleId = catalogBundleId
          upsertData.quantity = quantity

          const cartBundleConfigurator = new CartBundleConfigurator(
            bundle,
            mainStore.cadyMarket as CadyMarket
          )

          upsertData.selectedItems = cartBundleConfigurator.configureSelectedItems()
          upsertData.cartAttributes = buildCartUpsertAttributes(mainStore.cadySessionToken)

          try {
            this.setCartData(
              await sunny16CommerceCartService.upsertCartBundle(
                upsertData,
                mainStore.cadyMarket,
                this.cartId,
                this.cartSecurityKey,
                mainStore.subject?.SubjectId,
                mainStore.subject?.SchoolId,
                mainStore.subjectSessionEventDateAsIsoString,
                upsellId
              )
            )
          } catch (error) {
            if (isCartUpsertConcurrencyError(error)) return unchanged()
            throw error
          }
        }
      )
    },
    /**
     * Resolves once no cart call is in flight. For work that must not race a call the page load
     * started, such as the underclass default-background add on mount: Sunny16 rejects a concurrent
     * upsert on the same cart and `upsertCartBundle` swallows that rejection as a no-op.
     */
    async whenIdle(): Promise<void> {
      if (!this.isBusy) return

      await new Promise<void>((resolve) => {
        const stop = watch(
          () => this.isBusy,
          (busy) => {
            if (busy) return
            stop()
            resolve()
          }
        )
      })
    },
    async upsertCartBundle(
      cartBundle: CartBundleUpsertStorefrontDto,
      upsellId?: string,
      options?: {
        /**
         * Suppress this add's flyout for a reason only the response can settle — evaluated after the
         * upsert, against the updated cart. The retouching step uses it to stand the flyout down when
         * the add granted a reward that opens the claim modal instead.
         */
        suppressCartFlyout?: () => boolean
        /**
         * `false` for an add the storefront makes on the customer's behalf (the underclass default
         * background on mount). Runs under `runWithBusy` alone, so no mutation is announced and the
         * flyout's reward sequence does not read a page load as an add. Defaults to `true`.
         */
        customerAction?: boolean
      }
    ) {
      const upsert = async (unchanged: () => void = () => {}) => {
        const mainStore = useMainStore()
        cartBundle.cartAttributes = buildCartUpsertAttributes(mainStore.cadySessionToken)

        const send = () =>
          sunny16CommerceCartService.upsertCartBundle(
            cartBundle,
            mainStore.cadyMarket,
            this.cartId,
            this.cartSecurityKey,
            mainStore.subject?.SubjectId,
            mainStore.subject?.SchoolId,
            mainStore.subjectSessionEventDateAsIsoString,
            upsellId
          )

        try {
          this.setCartData(await send())
        } catch (error) {
          if (isCartUpsertConcurrencyError(error)) return unchanged()
          if (!isCartBundleNotFoundError(error) || !cartBundle.cartBundleId) throw error

          // The cart no longer has the bundle this update targets. Without this, a caller holding the
          // id in its own state re-sends the dead one on every retry and can never leave the step.
          // Rationale: `docs/features/2026.09.18-cart-state-reconciliation/implementation-plans/2026.09.18-001-cart-state-reconciliation.md`
          const staleCartBundleId = cartBundle.cartBundleId

          await this.refreshCart()

          // Re-target the same catalog bundle if the refreshed cart still has one, so a cart that
          // replaced the row is updated in place rather than given a second copy of it. `cartBundle`
          // is the caller's object and is rewritten deliberately: its next upsert must not carry the
          // dead id either.
          cartBundle.cartBundleId =
            this.cartBundles.find((cb) => cb.catalogBundleId === cartBundle.catalogBundleId)
              ?.cartBundleId ?? ''

          appErrorService.reportError(
            new Error('Cart bundle upsert targeted a cart bundle the cart no longer has'),
            {
              context: 'cart.upsertCartBundle',
              cartId: this.cartId ?? '',
              cartBundleId: staleCartBundleId,
              catalogBundleId: cartBundle.catalogBundleId,
              retriedAs: cartBundle.cartBundleId ? 'update' : 'insert',
              cartBundleCount: this.cartBundles.length
            }
          )

          this.setCartData(await send())
        }
      }

      if (options?.customerAction === false) {
        return runWithBusy(this, () => runCartWrite(this, () => upsert()))
      }

      // Same Gap 20 suppression as `addCartBundle` — this is the path the package builder takes when
      // it was opened from an upsell (`packageBuilderStore.upsellId`). A caller-supplied thunk is
      // ORed with it: an upsell add stays suppressed whatever the thunk says.
      return runCartMutation(
        this,
        {
          kind: CartMutationKind.Add,
          suppressCartFlyout: () => !!upsellId || (options?.suppressCartFlyout?.() ?? false)
        },
        upsert
      )
    },
    async updateCartBundle(cartBundleId: string, quantity: number) {
      return runCartMutation(this, { kind: CartMutationKind.Update }, async () => {
        this.setCartData(
          await sunny16CommerceCartService.updateCartBundleQuantity(
            this.cartId as string,
            this.cartSecurityKey as string,
            cartBundleId,
            quantity
          )
        )
      })
    },
    async updateCartConfiguration(cartConfiguration: CartBundleUpdateStorefrontDto[]) {
      return runWithBusy(this, () =>
        runCartWrite(this, async () => {
          this.setCartData(
            await sunny16CommerceCartService.updateCartConfiguration(
              this.cartId as string,
              this.cartSecurityKey as string,
              cartConfiguration
            )
          )
        })
      )
    },
    async emptyCart() {
      return runCartMutation(this, { kind: CartMutationKind.Empty }, async () => {
        this.setCartData(
          await sunny16CommerceCartService.deleteAllCartBundles(
            this.cartId as string,
            this.cartSecurityKey as string
          )
        )
      })
    },
    async decrementCartBundleQuantityOrRemove(cartBundleId: string) {
      // this method is used to decrement the quantity of a cart bundle
      // or remove it from the cart if the initial quantity is 1
      //
      // Stays on `runWithBusy`: it delegates to two actions that announce the mutation themselves, and
      // wrapping it as well would stamp two records for one customer action — read downstream as two
      // sequences, the second interrupting the first.
      return runWithBusy(this, async () => {
        const cartBundle = this.cartBundles.find((cb) => cb.cartBundleId === cartBundleId)

        if (!cartBundle) return

        if (cartBundle.quantity > 1) {
          await this.updateCartBundle(cartBundleId, cartBundle.quantity - 1)
        } else {
          await this.removeCartBundle(cartBundleId)
        }
      })
    },
    async removeCartBundle(cartBundleId: string) {
      return runCartMutation(this, { kind: CartMutationKind.Remove }, async (unchanged) => {
        try {
          this.setCartData(
            await sunny16CommerceCartService.deleteCartBundle(
              this.cartId as string,
              this.cartSecurityKey as string,
              cartBundleId
            )
          )
        } catch (error) {
          // Swallow the specific 400 the server returns when a duplicate DELETE
          // races a prior one — end state already matches intent.
          if (!isCartBundleNotFoundError(error)) throw error

          if (!(await this.reconcileMissingCartBundle(cartBundleId))) unchanged()
        }
      })
    },
    async removeCartBundleByCatalogBundleId(catalogBundleId: string) {
      // this method currently assumes that a catalog bundle has a 1:1 relationship with a cart bundle
      // (ie a catalog bundle will only ever be in a cart once)
      const cartBundle = this.cartBundles.find(
        (cartBundle) => cartBundle.catalogBundleId === catalogBundleId
      )

      if (!cartBundle) return

      return runCartMutation(this, { kind: CartMutationKind.Remove }, async (unchanged) => {
        try {
          this.setCartData(
            await sunny16CommerceCartService.deleteCartBundle(
              this.cartId as string,
              this.cartSecurityKey as string,
              cartBundle.cartBundleId
            )
          )
        } catch (error) {
          // Swallow the specific 400 the server returns when a duplicate DELETE
          // races a prior one — end state already matches intent.
          if (!isCartBundleNotFoundError(error)) throw error

          if (!(await this.reconcileMissingCartBundle(cartBundle.cartBundleId))) unchanged()
        }
      })
    },
    async upsertCartItemCustomizations(
      cartItemCustomizations: (CartItemCustomizationDto | CartItemImageCustomizationDto)[]
    ) {
      return runWithBusy(this, () =>
        runCartWrite(this, async () => {
          this.setCartData(
            await sunny16CommerceCartService.upsertCartCustomizations(
              this.cartId as string,
              this.cartSecurityKey as string,
              cartItemCustomizations
            )
          )
        })
      )
    },
    /**
     * The server says it has no such cart bundle. If the local copy still lists it, the two have
     * drifted — and this is the earliest point that is visible, well before a later write fails
     * against the same id. Refetch and report once so the drift is both repaired and countable.
     *
     * A duplicate-tap DELETE reaches here too, but harmlessly: the winning call already removed the
     * bundle from the local copy, so the check below is false and nothing happens.
     *
     * Returns whether it refetched, so a remove that did nothing can stay unannounced.
     */
    async reconcileMissingCartBundle(cartBundleId: string): Promise<boolean> {
      if (!this.cartBundles.some((cb) => cb.cartBundleId === cartBundleId)) return false

      appErrorService.reportError(
        new Error('Cart bundle missing on the server but present in the local cart'),
        {
          context: 'cart.reconcileMissingCartBundle',
          cartId: this.cartId ?? '',
          cartBundleId,
          cartBundleCount: this.cartBundles.length
        }
      )

      await this.refreshCart()
      return true
    },
    /**
     * Checks the persisted cart against the server once, at hydration.
     *
     * A cart restored from localStorage is a claim about the server, not a fact — and until this
     * existed nothing ever checked it. A write whose response the customer never received (a slow
     * network, a backgrounded tab, a reload) leaves the two permanently disagreeing, and every
     * later cart call fails against ids the server does not have. Rationale:
     * `docs/features/2026.09.18-cart-state-reconciliation/implementation-plans/2026.09.18-001-cart-state-reconciliation.md`
     *
     * Runs under `refreshCart`'s `runWithBusy`, so cart CTAs are disabled while it is in flight and
     * the customer cannot act on the stale copy in the meantime.
     *
     * Only a definite "this cart does not exist" clears local state. Any other failure leaves the
     * persisted cart alone: a transient error at boot is not evidence the cart is gone, and the
     * axios interceptor has already reported it.
     *
     * Skipped in the tab that holds a payment: it releases the hold as it loads, and this `GET`
     * landing after the release would write the held cart back.
     */
    async revalidatePersistedCart(): Promise<void> {
      if (!this.cartId || !this.cartSecurityKey) return
      if (tabHoldsPayment(this.cartData?.order?.orderId)) return

      const generation = this.generation

      try {
        await this.refreshCart()
      } catch (error) {
        if (isCartGoneError(error) && this.generation === generation) this.cartData = null
        return
      }

      if (this.generation !== generation) return

      // A response that carries no cart is the same answer by another route.
      if (!this.cartData?.cartId) this.cartData = null
    },
    /** Replaces `cartData` with the server's copy; used after a failed mutation so the page and any retry start from what the server has. */
    async refreshCart() {
      if (!this.cartId || !this.cartSecurityKey) return

      const generation = this.generation

      return runWithBusy(this, async () => {
        const cart = await sunny16CommerceCartService.getCart(
          this.cartId as string,
          this.cartSecurityKey as string
        )

        // The hydration revalidation is still in flight when a new `?s=` token resets the store;
        // applying it then would hand the new session the previous subject's cart.
        if (this.generation !== generation) return

        this.setCartDataKeepingUpsells(cart)
      })
    },
    /**
     * Refetches a cart this tab shows as held for a payment, so a hold that ended elsewhere — released
     * by the paying tab, or expired — doesn't leave this tab blocked with no way to notice. Skipped
     * in the tab that holds the payment, which releases its own hold.
     *
     * A cart still held and unpaid is not written: nothing else can have changed it, and writing it
     * posts `cart_updated`, which reloads every other tab, the paying one included, and that tab
     * releases its hold on load. `PaymentHoldModal` re-checks every few seconds, so this matters.
     */
    async recheckPaymentHold() {
      if (!this.isHeldForPayment || tabHoldsPayment(this.cartData?.order?.orderId)) return
      if (!this.cartId || !this.cartSecurityKey) return

      const generation = this.generation

      paymentHoldRecheck ??= (async () => {
        try {
          await runWithBusy(this, async () => {
            const cart = await sunny16CommerceCartService.getCart(
              this.cartId as string,
              this.cartSecurityKey as string
            )

            if (this.generation !== generation) return

            const stillHeld =
              cart.isOperationallyLocked &&
              cart.operationalLockReason === CartLockReason.Payment &&
              !cart.isLocked
            if (stillHeld) return

            this.setCartDataKeepingUpsells(cart)
          })
        } catch {
          // Keep showing the hold; the next re-check, or a refused change, tries again.
        } finally {
          paymentHoldRecheck = null
        }
      })()

      return paymentHoldRecheck
    },
    /**
     * `setCartData` for a cart from a call that does not compute `activeUpsells` — `GET cart` and the
     * checkout payment calls; only mutations and `/prepare` do. Keeps the last computed list rather
     * than hiding the flow step's upsells until the next write.
     */
    setCartDataKeepingUpsells(cart: CartStorefrontDto | null): void {
      if (cart && cart.activeUpsells == null) {
        cart.activeUpsells = this.cartData?.activeUpsells ?? null
      }

      this.setCartData(cart)
    },
    // Runs on every checkout landing: `/prepare` materialises `cartData.order`
    // (customer details, totals), which `CheckoutView.beforeMount()` reads
    // straight after, so it cannot be skipped once the cart has been edited.
    // Only the in-cart upsell auto-add inside it is once-per-cart. Sunny16 owns
    // the add; the storefront owns the memory of whether it has already been
    // asked for this cart, and tells Sunny16 via `applyInCartUpsells`. The
    // latch is set after a successful response so a failed first prepare is
    // retried with upsells enabled on the next landing.
    //
    // `options.applyInCartUpsells` overrides the latch; `reapplyInCartUpsells()`
    // passes `true` when the customer re-checks a declined upsell.
    async prepareCart(options: { applyInCartUpsells?: boolean } = {}) {
      const generation = this.generation

      return runWithBusy(this, async () => {
        const cartId = this.cartId as string
        const applyInCartUpsells =
          options.applyInCartUpsells ?? this.inCartUpsellsAppliedForCartId !== cartId

        const cart = await sunny16CommerceCartService.prepareCart(
          cartId,
          this.cartSecurityKey as string,
          applyInCartUpsells
        )

        // A `/prepare` still in flight when a new `?s=` token resets the store belongs to the
        // previous subject's cart.
        if (this.generation !== generation) return

        this.setCartData(cart)
        this.inCartUpsellsAppliedForCartId = cartId
      })
    },
    // Checkout in-cart upsell re-checked. Re-runs `/prepare` with the auto-add
    // enabled so Sunny16 rebuilds the bundle exactly as it did on first landing
    // (`Source=AutomaticUpsell`, its own item selection); the storefront never
    // constructs the bundle itself. A `true` applies every in-cart upsell the
    // catalog configures, not just the one re-checked. Exact while a catalog
    // configures one; a second would need the Sunny16 parameter to grow a
    // target bundle id.
    //
    // A customer's change, so a failure refetches the cart as other cart writes do. Checkout's own
    // `prepareCart` on load recovers in `CheckoutView` instead.
    async reapplyInCartUpsells(): Promise<void> {
      await runCartWrite(this, () => this.prepareCart({ applyInCartUpsells: true }))
    },
    async validateCart(): Promise<CartStorefrontValidationResultDto> {
      return await sunny16CommerceCartService.validateCart(
        this.cartId as string,
        this.cartSecurityKey as string
      )
    },
    async applyPromoCode(promoCode: string): Promise<Partial<PromoCodeResultStorefrontDto>> {
      return runWithBusy(this, async () => {
        const dataRequest = new ApplyPromoCodeStorefrontDto()

        dataRequest.code = promoCode

        // A failed request rejects rather than reading as a refused code, so the form can tell a
        // busy or unreachable cart apart from a code that does not apply.
        const response = await sunny16CommerceCartService.applyPromoCode(
          this.cartId as string,
          this.cartSecurityKey as string,
          dataRequest
        )

        if (response && response.isApplied) {
          this.setCartData(response.cart)
        }

        return {
          isApplied: response.isApplied,
          message: response.message
        }
      })
    },
    markPortfolioAutoAddAttempted(): void {
      this.portfolioAutoAddAttemptedThisSession = true
    },
    /**
     * The single ingestion point for `cartData`. Every cart the app loads — a mutation response, the
     * `getCart` on checkout, a customer-service order adjustment — is assigned through here, so the
     * normalisation below applies to all of them.
     *
     * Normalisation: each bundle's `cartItems` are sorted by `zOrder`. This used to happen as a side
     * effect inside `commerceStore.cartBundlesRequiringConfiguration`, which reassigned the array on
     * every evaluation — a getter writing cart state on every render, and firing the persistence
     * plugin and the cart subscription each time it did. Sorting the DTO here, before it is reactive,
     * keeps the order every consumer relied on without the write. See decision
     * `docs/features/2026.09.11-store-layering/decisions/2026.09.11-001-pinia-store-layering.md`.
     */
    setCartData(cart: CartStorefrontDto | null): void {
      if (cart?.cartBundles) {
        for (const cartBundle of cart.cartBundles) {
          if (cartBundle.cartItems) {
            cartBundle.cartItems = orderBy(cartBundle.cartItems, ['zOrder'], ['asc'])
          }
        }
      }

      this.cartData = cart
    },
    resetStore(): void {
      // This method is used to reset the store to its initial state manually because of the use of persist
      // Any state property added above should be reset here
      this.cartData = null
      this.inFlight = 0
      this.generation++
      this.lastCartMutation = null
      this.portfolioAutoAddAttemptedThisSession = false
      this.inCartUpsellsAppliedForCartId = null
    }
  },
  persist: {
    // Fire-and-forget on purpose: hydration must not block, and the revalidation swallows its own
    // failures, so there is no rejection to handle. `afterHydrate` is the one point that means
    // "a persisted cart was just restored" — no route coupling, no TTL, and it cannot run twice.
    afterHydrate(ctx) {
      if (readLegacyPortfolioLatch()) ctx.store.portfolioAutoAddAttemptedThisSession = true

      if (!ctx.store.cartData?.cartId) return

      void ctx.store.revalidatePersistedCart()
    },
    // Omit `inFlight` so a mid-call refresh rehydrates the counter to 0
    // instead of leaving the cart UI frozen. `generation` only has meaning within one page load.
    //
    // `lastCartMutation` is omitted for the same shape of reason: it means "the customer just changed
    // the cart", and a refresh is a new page rather than a new mutation. Restoring it would leave a
    // record on the very first evaluation after load, which is what a consumer baselines against.
    omit: ['inFlight', 'generation', 'lastCartMutation']
  }
})

// Every reader of an incomplete `UC_Background` bundle sits on a render path, and the two getters
// that reach them return closures a cart row invokes on each re-render — so an unlatched report
// would fire once per row per render. Latch per `cartBundleId` so New Relic gets one record per
// offending bundle. Deliberately never cleared: the bundle's shape only changes when the server
// sends a new cart, and by then the customer has moved on from the render that would re-report it.
const reportedIncompleteUCBackgroundBundleIds = new Set<string>()

// Keeps the empty-state visible instead of letting the guards below fail silently. `cartId` and
// `sourcePromotionId` are the attributes worth having: the New Relic error that prompted these
// guards carried neither, so there was no way to pull the offending cart and work out which add
// path produced a background bundle with nothing selected on it.
function reportIncompleteUCBackgroundBundle(
  backgroundBundle: CartBundleStorefrontDto,
  detail: string
): void {
  if (reportedIncompleteUCBackgroundBundleIds.has(backgroundBundle.cartBundleId)) {
    return
  }

  reportedIncompleteUCBackgroundBundleIds.add(backgroundBundle.cartBundleId)

  appErrorService.reportError(new Error(`Incomplete UC_Background cart bundle: ${detail}`), {
    cartBundleId: backgroundBundle.cartBundleId,
    cartId: backgroundBundle.cartId,
    catalogBundleId: backgroundBundle.catalogBundleId,
    sourcePromotionId: backgroundBundle.sourcePromotionId,
    requiredParentCartBundleId: backgroundBundle.requiredParentCartBundleId,
    cartItemCount: backgroundBundle.cartItems?.length ?? 0,
    quantity: backgroundBundle.quantity
  })
}

// Assumes the cart bundle is the underclass background bundle and extracts the selected background
// color value. Returns `null` — rather than throwing — when the bundle carries no selection.
//
// That is a real server state, not a can't-happen: the background item group is optional
// (`minRequiredItems` 0, priced through `optionalItemPrices`), so `CartBundleConfigurator`
// contributes no cart items for it and any add that doesn't route through
// `PoseBackgroundSelectionBundle` lands a bundle row nobody made a selection on. The rest of the
// codebase already tolerates the shape — `mapCartBundleToCartBundleUpsert` maps it to an empty
// `selectedItems`, and `PoseBackgroundSelectionBundle.mounted()` branches on it to default to the
// first available background — so the unguarded dereference here was the outlier.
export function getUCBackgroundItem(
  backgroundBundle: CartBundleStorefrontDto
): UCBackgroundResult | null {
  // There's only one cart item in the underclass background bundle
  const cartItem = backgroundBundle.cartItems?.[0]

  if (!cartItem) {
    reportIncompleteUCBackgroundBundle(backgroundBundle, 'no cart items')
    return null
  }

  // Get the selected background color from the prompt response
  const promptResponse = cartItem.cartItemCustomizationDetails?.promptResponses?.[0] as
    | PromptResponseDto
    | undefined

  if (!promptResponse?.value) {
    reportIncompleteUCBackgroundBundle(backgroundBundle, 'no background color prompt response')
    return null
  }

  return { value: promptResponse.value, cartItem, promptResponse }
}
